<script setup lang="ts">
	import { useUsersCompanyStore } from '@/stores/usersCompany'
	import { onClickOutside, useDebounceFn } from '@vueuse/core'
	import { Check, ChevronDown, Search, UserCheck, UserX, X } from 'lucide-vue-next'
	import { storeToRefs } from 'pinia'
	import { computed, nextTick, ref, watch } from 'vue'

	import { Button } from '@/components/ui/button'
	import {
		Tooltip,
		TooltipContent,
		TooltipProvider,
		TooltipTrigger,
	} from '@/components/ui/tooltip'
	import LoadingSpin from '@/components/icons/LoadingSpin.vue'

	import useAuthUser from '@/composables/useAuthUser'

	import useUsersSearch from '../composables/useUsersSearch'
	import { User } from '../interfaces/user'

	// Filtro "Supervisado por": botón compacto (estilo consistente con el hero)
	// que abre un popover propio con buscador + lista de supervisores.
	// Solo visible para Master y Travel Manager (respetando jerarquía);
	// Supervisor y Viajero no lo ven.
	const { user } = useAuthUser()
	const usersCompanyStore = useUsersCompanyStore()
	const { filters, supervisorUsers } = storeToRefs(usersCompanyStore)

	const canFilterBySupervisor = computed(() => {
		const roles = (user.value?.roles || []).map((r) => r.toLowerCase())
		return roles.includes('master') || roles.includes('travel manager')
	})

	const open = ref(false)
	const query = ref('')
	const root = ref<HTMLElement | null>(null)
	const searchInput = ref<HTMLInputElement | null>(null)

	// El endpoint /users/search ya limita por bloodline/descendientes;
	// supervisors=['Traveler'] devuelve los roles por encima de Viajero
	// (Master, Travel Manager, Supervisor) vía getRoleHierarchy(asc).
	const supervisorSearchFilter = computed(() => ({
		company: filters.value.company,
		supervisors: ['Traveler'],
	}))

	const {
		users: supervisorUsersRaw,
		setQuery: setSupervisorQuery,
		isFetching: fetchingSupervisors,
		refetch,
	} = useUsersSearch({
		params: { filters: supervisorSearchFilter },
		immediate: false,
	})

	// De mayor a menor subordinados, para ver primero a quién ya supervisa más.
	// `supervisorOptions` (candidatos de la búsqueda), no `supervisorUsers`: ese
	// nombre ya lo usa el store para los que están SELECCIONADOS.
	const supervisorOptions = computed(() =>
		[...(supervisorUsersRaw.value ?? [])].sort(
			(a, b) => (b.subordinatesCount ?? 0) - (a.subordinatesCount ?? 0)
		)
	)

	const runSearch = useDebounceFn(() => refetch(), 350)

	watch(query, (value) => {
		setSupervisorQuery(value)
		runSearch()
	})

	const initials = (u: User) =>
		`${u.name?.[0] ?? ''}${u.lastname?.[0] ?? ''}`.toUpperCase()

	const roleLabel = (u: User) => u.roles?.[0] ?? ''

	const toggleOpen = async () => {
		open.value = !open.value
		if (open.value) {
			// Carga inicial de opciones y foco al buscador.
			refetch()
			await nextTick()
			searchInput.value?.focus()
		}
	}

	/**
	 * Multi-selección: el popover NO se cierra al elegir, para poder marcar varios
	 * seguidos. Se cierra con clic afuera o con el botón.
	 */
	const selectSupervisor = (u: User) => {
		// Elegir a alguien y "sin supervisor" son excluyentes: juntos no devuelven
		// nada, porque nadie tiene un supervisor concreto y a la vez ninguno.
		filters.value.withoutSupervisor = undefined
		usersCompanyStore.toggleSupervisorUser(u)
	}

	const isSelected = (u: User) =>
		supervisorUsers.value.some((selected) => selected.id === u.id)

	const clearSupervisor = () => {
		usersCompanyStore.setSupervisorUsers([])
		filters.value.withoutSupervisor = undefined
		open.value = false
		query.value = ''
	}

	/**
	 * "Sin supervisor" no es un supervisor más de la lista: es una bandera aparte
	 * (`withoutSupervisor`). El backend ignora `supervisor=` vacío, así que no se
	 * puede modelar como "seleccionar a nadie".
	 */
	const selectWithoutSupervisor = () => {
		usersCompanyStore.setSupervisorUser(undefined)
		filters.value.withoutSupervisor = true
		open.value = false
		query.value = ''
	}

	const isWithoutSupervisorActive = computed(
		() => !!filters.value.withoutSupervisor
	)

	/**
	 * Con uno se muestra el nombre; con varios, el primero más el conteo, porque
	 * concatenar tres nombres no cabe en la píldora y se corta a la mitad.
	 */
	const pillLabel = computed(() => {
		if (isWithoutSupervisorActive.value) return 'Sin supervisor'

		const [first, ...rest] = supervisorUsers.value
		if (!first) return 'Supervisado por'

		const name = `${first.name} ${first.lastname}`

		return rest.length ? `${name} +${rest.length}` : name
	})

	/**
	 * Suma de subordinados de los supervisores elegidos. Es una referencia, no el
	 * conteo de la tabla: cuenta a quienes dependen de cada uno, sin importar si el
	 * resto de filtros activos (estado, rol, departamento) los deja fuera.
	 */
	const totalSupervisedUsers = computed(() =>
		supervisorUsers.value.reduce(
			(total, supervisor) => total + (supervisor.subordinatesCount ?? 0),
			0
		)
	)

	onClickOutside(root, () => {
		open.value = false
	})
</script>

<template>
	<div
		v-if="canFilterBySupervisor"
		ref="root"
		class="relative"
	>
		<!-- Pill con el filtro activo: un supervisor concreto, o "Sin supervisor".
		     Los dos tienen que verse aquí; si no, el filtro queda aplicado y el botón
		     sigue diciendo "Supervisado por" como si nada estuviera puesto. -->
		<!-- La píldora resume ("Ana García +2"), así que sin el tooltip no hay forma de
		     saber quiénes son los otros sin volver a abrir el popover. El disparador
		     es la píldora COMPLETA, no sólo el texto: apuntarle a un target de un
		     renglón es innecesariamente fino. -->
		<!-- El `v-if` va AQUÍ y no en el hijo del TooltipTrigger: con `as-child` el
		     trigger toma el ref del elemento hijo para anclar el popper, y si ese hijo
		     no existe al montar se queda sin ancla y el tooltip aparece pegado a la
		     esquina superior izquierda de la pantalla en vez de junto a la píldora. -->
		<TooltipProvider
			v-if="supervisorUsers.length || isWithoutSupervisorActive"
			:delay-duration="200"
		>
			<Tooltip>
				<TooltipTrigger as-child>
					<div
						class="inline-flex h-9 items-center gap-2 rounded-md border border-blue-200 bg-blue-50 pl-3 pr-1.5 text-sm font-medium text-blue-700 max-w-[240px]"
					>
						<UserX
							v-if="isWithoutSupervisorActive"
							class="h-4 w-4 shrink-0"
						/>
						<UserCheck
							v-else
							class="h-4 w-4 shrink-0"
						/>
						<button
							class="truncate"
							@click="toggleOpen"
						>
							{{ pillLabel }}
						</button>
						<button
							class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full hover:bg-white/70"
							aria-label="Quitar filtro de supervisor"
							@click="clearSupervisor"
						>
							<X class="h-3.5 w-3.5" />
						</button>
					</div>
				</TooltipTrigger>
				<TooltipContent
					side="bottom"
					align="start"
					:side-offset="6"
					class="max-w-[260px]"
				>
						<p
							v-if="isWithoutSupervisorActive"
							class="text-xs"
						>
							Mostrando usuarios que no tienen ningún supervisor asignado.
						</p>
						<div v-else>
							<p class="text-xs font-semibold mb-1">
								{{
									supervisorUsers.length === 1 ?
										'Supervisado por'
									:	`Supervisado por ${supervisorUsers.length} personas`
								}}
							</p>
							<ul class="space-y-0.5">
								<li
									v-for="supervisor in supervisorUsers"
									:key="supervisor.id"
									class="text-xs"
								>
									{{ supervisor.name }} {{ supervisor.lastname }}
									<span class="opacity-70">
										— {{ supervisor.subordinatesCount ?? 0 }}
										{{
											(supervisor.subordinatesCount ?? 0) === 1 ?
												'usuario'
											:	'usuarios'
										}}
									</span>
								</li>
							</ul>
							<p class="text-xs opacity-70 mt-1.5 border-t border-white/20 pt-1">
								Total: {{ totalSupervisedUsers }}
								{{ totalSupervisedUsers === 1 ? 'usuario' : 'usuarios' }}
							</p>
						</div>
					</TooltipContent>
			</Tooltip>
		</TooltipProvider>

		<!-- Botón por defecto -->
		<Button
			v-if="!supervisorUsers.length && !isWithoutSupervisorActive"
			variant="outline"
			class="h-9 gap-2"
			@click="toggleOpen"
		>
			<UserCheck class="h-4 w-4" />
			<span>Supervisado por</span>
			<ChevronDown class="h-4 w-4 opacity-70" />
		</Button>

		<!-- Popover -->
		<div
			v-if="open"
			class="absolute left-0 top-11 z-30 w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-lg"
		>
			<div
				class="mb-1.5 flex h-9 items-center gap-2 rounded-md border border-slate-200 px-2.5 focus-within:border-blue-400"
			>
				<Search class="h-4 w-4 shrink-0 text-slate-400" />
				<input
					ref="searchInput"
					v-model="query"
					type="text"
					placeholder="Buscar supervisor..."
					class="h-full w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
				/>
				<LoadingSpin
					v-if="fetchingSupervisors"
					class="h-4 w-4 shrink-0 animate-spin text-slate-400"
					:dark="true"
				/>
			</div>

			<div class="max-h-56 overflow-y-auto">
				<!-- Fijo arriba y fuera del buscador: no es un supervisor, es la
				     ausencia de uno, así que filtrar por texto no le aplica. -->
				<button
					class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-slate-50"
					:class="isWithoutSupervisorActive ? 'bg-blue-50' : ''"
					@click="selectWithoutSupervisor"
				>
					<span
						class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400"
					>
						<UserX class="h-4 w-4" />
					</span>
					<span class="min-w-0 flex-1">
						<span
							class="block truncate text-sm"
							:class="
								isWithoutSupervisorActive ?
									'font-medium text-blue-700'
								:	'text-slate-800'
							"
						>
							Sin supervisor
						</span>
						<span class="block truncate text-xs text-slate-400">
							Usuarios que no tienen ninguno asignado
						</span>
					</span>
				</button>

				<div class="my-1 border-t border-slate-100" />

				<button
					v-for="u in supervisorOptions"
					:key="u.id"
					class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-slate-50"
					:class="isSelected(u) ? 'bg-blue-50' : ''"
					:aria-pressed="isSelected(u)"
					@click="selectSupervisor(u)"
				>
					<span
						class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[11px] font-medium text-blue-700"
					>
						{{ initials(u) }}
					</span>
					<span class="min-w-0 flex-1">
						<span
							class="block truncate text-sm"
							:class="
								isSelected(u) ? 'font-medium text-blue-700' : 'text-slate-800'
							"
						>
							{{ u.name }} {{ u.lastname }}
						</span>
						<span
							v-if="roleLabel(u)"
							class="block truncate text-xs text-slate-400"
						>
							{{ roleLabel(u) }} · {{ u.subordinatesCount ?? 0 }} subordinados
						</span>
					</span>
					<Check
						v-if="isSelected(u)"
						class="h-4 w-4 shrink-0 text-blue-600"
					/>
				</button>

				<p
					v-if="!fetchingSupervisors && supervisorOptions.length === 0"
					class="px-2 py-3 text-center text-xs text-slate-400"
				>
					{{ query ? 'Sin resultados' : 'Escribe para buscar…' }}
				</p>
			</div>
		</div>
	</div>
</template>
